/**
 * A corrida entre o deep-link e o Flagsmith.
 *
 * `modulosEFuncionalidades` nasce POPULADO com os valores padrão do sistema
 * (`src/core/modulos`), e alguns deles nascem desligados — o Assistente é um
 * deles. Os valores reais do tenant só chegam quando o Flagsmith responde, no
 * `onChange` que dispara `DEFINIR_MODULOS` e `DEFINIR_FUNCIONALIDADES`; e essa
 * inicialização acontece depois do login, atrás de um `setTimeout`.
 *
 * Uma navegação vinda de dentro do app já encontra tudo no lugar. Um F5 ou um
 * endereço colado na barra, não: a guarda de requisitos roda na janela em que
 * o store ainda serve o padrão, reprova um requisito que a empresa TEM e joga
 * a pessoa na Home com "você não possui o módulo necessário".
 *
 * A espera aqui é o remédio mínimo: ela só acontece quando a validação já
 * falhou E as flags ainda não chegaram nenhuma vez. Depois da primeira carga,
 * `aguardar` devolve imediatamente para sempre. Se o Flagsmith não responder
 * dentro do limite, desistimos de vez — a partir daí a validação volta a ser o
 * que sempre foi, fechada sobre os valores padrão.
 */

/** Limite da espera. Generoso: o custo dela é pago no máximo uma vez. */
export const LIMITE_DE_ESPERA_PELOS_REQUISITOS_MS = 7000

/** Mutações que marcam a chegada dos requisitos reais do tenant. */
export const MUTACOES_DE_CARGA_DOS_REQUISITOS: string[] = [
  'DEFINIR_MODULOS',
  'DEFINIR_FUNCIONALIDADES'
]

interface MutacaoObservada { type?: string }
interface StoreObservavel {
  subscribe: (ouvinte: (mutacao: MutacaoObservada) => void) => unknown
}

/**
 * O store chega por thunk, não por referência, e a inscrição é preguiçosa.
 *
 * `src/store` importa `baseStates/userInfo`, que importa `src/main`, que
 * importa este router: quando o ciclo aterrissa aqui, o módulo do store ainda
 * está no meio da própria avaliação e a importação vale `undefined`. Tocar em
 * `store.subscribe` no corpo do módulo derruba o app inteiro na carga — tela
 * branca, `Cannot read properties of undefined (reading 'subscribe')`.
 *
 * Por isso ninguém resolve o store na criação. `garantirEscuta` é idempotente,
 * tolera store ausente (tenta de novo na próxima chamada) e é chamada a cada
 * navegação: a primeira delas já deixa a escuta de pé, muito antes de o
 * Flagsmith responder.
 */
type ObterStore = () => StoreObservavel | null | undefined

export interface EsperaPelosRequisitos {
  /** Resolve quando as flags do tenant chegarem, ou quando o limite vencer. */
  aguardar: () => Promise<void>
  /** Se as duas mutações de carga já passaram pelo store. */
  jaCarregou: () => boolean
  /** Inscreve no store se ainda não deu — segura de chamar sempre. */
  garantirEscuta: () => void
}

export function criarEsperaPelosRequisitos (
  obterStore: ObterStore,
  opcoes: { limiteEmMs?: number } = {}
): EsperaPelosRequisitos {
  const limiteEmMs = opcoes.limiteEmMs ?? LIMITE_DE_ESPERA_PELOS_REQUISITOS_MS
  const recebidas = new Set<string>()
  const ouvintes = new Set<() => void>()
  let desistimos = false
  let inscrito = false

  const jaCarregou = (): boolean =>
    MUTACOES_DE_CARGA_DOS_REQUISITOS.every((tipo) => recebidas.has(tipo))

  function garantirEscuta (): void {
    if (inscrito) return
    const store = obterStore()
    if (!store || typeof store.subscribe !== 'function') return

    store.subscribe((mutacao) => {
      const tipo = mutacao?.type
      if (!tipo || !MUTACOES_DE_CARGA_DOS_REQUISITOS.includes(tipo)) return
      recebidas.add(tipo)
      if (!jaCarregou()) return
      for (const avisar of [...ouvintes]) avisar()
    })
    inscrito = true
  }

  function aguardar (): Promise<void> {
    garantirEscuta()
    if (jaCarregou() || desistimos) return Promise.resolve()

    return new Promise<void>((resolver) => {
      const concluir = (): void => {
        clearTimeout(temporizador)
        ouvintes.delete(concluir)
        resolver()
      }
      const temporizador: ReturnType<typeof setTimeout> = setTimeout(() => {
        desistimos = true
        concluir()
      }, limiteEmMs)
      ouvintes.add(concluir)
    })
  }

  return { aguardar, jaCarregou, garantirEscuta }
}

/**
 * Valida, e só espera se tiver reprovado. Quem passa de primeira nunca paga o
 * custo da espera; quem reprova é reavaliado com as flags na mão. A segunda
 * validação é a que decide — sem ela, esperar seria abrir a porta.
 */
export async function requisitosAtendidos (
  validar: () => boolean,
  espera: EsperaPelosRequisitos
): Promise<boolean> {
  if (validar()) return true
  await espera.aguardar()
  return validar()
}
